import { useState } from "react";
import { useStore } from "@/hooks/useStore";
import { useAuth } from "@/hooks/useAuth";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Input, Textarea, Label } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { uid } from "@/lib/utils";
import type { GlossaryEntry } from "@/types/reference";
import { toast } from "sonner";

export function Glossary() {
  const { state, setState } = useStore();
  const { profile } = useAuth();
  const isAdmin = profile?.role === "admin";
  const entries = state.referenceData.glossary ?? [];
  const [draft, setDraft] = useState<Omit<GlossaryEntry, "id">>({ term: "", definition: "", category: "" });

  function add() {
    if (!draft.term.trim()) return;
    const e: GlossaryEntry = { id: uid("g_"), ...draft };
    setState((s) => ({
      ...s,
      referenceData: { ...s.referenceData, glossary: [...(s.referenceData.glossary ?? []), e] },
    }));
    setDraft({ term: "", definition: "", category: "" });
    toast.success("Term added");
  }

  function remove(id: string) {
    setState((s) => ({
      ...s,
      referenceData: { ...s.referenceData, glossary: s.referenceData.glossary.filter((g) => g.id !== id) },
    }));
  }

  function update(id: string, patch: Partial<GlossaryEntry>) {
    setState((s) => ({
      ...s,
      referenceData: {
        ...s.referenceData,
        glossary: s.referenceData.glossary.map((g) => (g.id === id ? { ...g, ...patch } : g)),
      },
    }));
  }

  const sorted = entries.slice().sort((a, b) => a.term.localeCompare(b.term));

  return (
    <div className="space-y-4">
      <PageTitle>Glossary ({entries.length})</PageTitle>

      {isAdmin && (
        <Panel className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Term</Label>
            <Input value={draft.term} onChange={(e) => setDraft({ ...draft, term: e.target.value })} />
          </div>
          <div>
            <Label>Category</Label>
            <Input value={draft.category ?? ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
          </div>
          <div className="sm:col-span-3">
            <Label>Definition</Label>
            <Textarea rows={2} value={draft.definition} onChange={(e) => setDraft({ ...draft, definition: e.target.value })} />
          </div>
          <div><Button onClick={add}>Add term</Button></div>
        </Panel>
      )}

      <div className="grid gap-3">
        {sorted.length === 0 ? (
          <Panel><p className="text-sm text-[#64748b]">No glossary entries yet.</p></Panel>
        ) : (
          sorted.map((g) => (
            <Panel key={g.id}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {isAdmin ? (
                    <>
                      <Input className="mb-2" value={g.term} onChange={(e) => update(g.id, { term: e.target.value })} />
                      <Textarea rows={2} value={g.definition} onChange={(e) => update(g.id, { definition: e.target.value })} />
                    </>
                  ) : (
                    <>
                      <p className="font-display text-base font-semibold">{g.term}</p>
                      <p className="mt-1 text-sm text-[#cbd5e1]">{g.definition}</p>
                      {g.category && <p className="text-xs text-[#64748b] mt-1">{g.category}</p>}
                    </>
                  )}
                </div>
                {isAdmin && <Button variant="danger" size="sm" onClick={() => remove(g.id)}>Delete</Button>}
              </div>
            </Panel>
          ))
        )}
      </div>
    </div>
  );
}
